/* eslint-disable react/forbid-component-props */
import { CarouselSlide } from '@/components/blocks/Carousel/CarouselSlide';
import { DpBenefitsDrawer } from '@/components/blocks/DpBenefits';
import {
	DpBenefitsProvider,
	useDpBenefits,
	useDpBenefitsContext,
} from '@/components/blocks/DpBenefitsContext';
import {
	BenefitsGrayContentSX,
	BenefitsGrayDescriptionSX,
	BenefitsGrayFrameSX,
	BenefitsGrayItemSX,
	BenefitsGraySlideInnerSX,
	BenefitsGrayTitleSX,
} from '@/components/blocks/DpBenefitsGray/styles/main';
import { DpBottomContainer } from '@/components/blocks/DpBottomContainer';
import { Img } from '@/components/blocks/MaterialImage';
import { combineSX } from '@/utils/combineSX';
import { parseHTML } from '@/utils/parseHTML';
import { Stack, SxProps, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CarouselProvider, Slider } from 'pure-react-carousel';
import React, { Fragment } from 'react';

const ITEMS = [
	{
		title: '30 días para cambios y devoluciones',
		description: 'sin complicaciones',
		icon: 'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/diseno/icons/bg10days.png',
		spot: 'DrawerReturnsExchangesInformative',
	},
	{
		title: 'Envío gratis a todo méxico',
		description: 'en compras +$999',
		icon: 'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/diseno/icons/bgshipping.png',
		spot: 'DrawerDeliveriesShipmentsInformative',
	},
	{
		title: 'Compra en línea y paga en efectivo',
		description: 'en OXXO y BBVA',
		icon: 'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/diseno/icons/bgpaymentmoney.png',
		spot: 'DrawerPaymentMethodsInformative',
	},
	{
		title: 'Elige el método de pago que prefieras',
		description: 'pagos sin preocupaciones',
		icon: 'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/diseno/icons/bgpaymetcard.png',
		spot: 'DrawerHowToBuy',
	},
];

const defaultCarouselProps = {
	naturalSlideWidth: 248,
	naturalSlideHeight: 300,
	visibleSlides: 1,
	step: 1,
	infinite: true,
	dragEnabled: false,
	totalSlides: 1,
	isIntrinsicHeight: true,
};

export const DpBenefitGray: React.FC<{
	title: string;
	description: string;
	spot: string;
	src: string;
}> = ({ title, description, spot, src }) => {
	const { onOpenDrawer } = useDpBenefitsContext();

	return (
		<Stack
			sx={BenefitsGrayItemSX}
			onClick={() =>
				onOpenDrawer({
					title,
					emsName: spot,
				})
			}
		>
			<Img src={src} alt={title} sx={{ width: '32px', objectFit: 'cover' }} />
			<Typography sx={BenefitsGrayTitleSX}>{parseHTML(title)}</Typography>
			<Typography sx={BenefitsGrayDescriptionSX}>{parseHTML(description)}</Typography>
		</Stack>
	);
};

interface DpBenefitsGrayFrameProps {
	framesx?: SxProps;
	sx?: SxProps;
	children?: React.ReactNode;
}

export const DpBenefitsGrayFrame: React.FC<DpBenefitsGrayFrameProps> = ({
	framesx,
	sx,
	children,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const cleanChildren = React.Children.toArray(children).filter((child) =>
		React.isValidElement(child)
	);

	return (
		<DpBottomContainer
			benefitGray
			framesx={combineSX([BenefitsGrayFrameSX, ...(Array.isArray(framesx) ? framesx : [framesx])])}
			sx={combineSX([BenefitsGrayContentSX, ...(Array.isArray(sx) ? sx : [sx])])}
		>
			{isMobile ? (
				<CarouselProvider
					isPlaying
					interval={8000}
					{...defaultCarouselProps}
					totalSlides={ITEMS.length}
				>
					<Slider style={{ overflow: 'hidden' }}>
						{cleanChildren.length > 0 ? (
							<Fragment>
								{cleanChildren.map((item, index) => (
									<CarouselSlide
										index={index}
										key={`id_carousel_${index}`}
										sx={BenefitsGraySlideInnerSX}
									>
										{item}
									</CarouselSlide>
								))}
							</Fragment>
						) : (
							<Fragment>
								{ITEMS.map((item, index) => (
									<CarouselSlide
										index={index}
										key={`id_carousel_${index}`}
										sx={BenefitsGraySlideInnerSX}
									>
										<DpBenefitGray
											key={item.title}
											title={item.title}
											description={item.description}
											spot={item.spot}
											src={item.icon}
										/>
									</CarouselSlide>
								))}
							</Fragment>
						)}
					</Slider>
				</CarouselProvider>
			) : (
				<>
					{cleanChildren.length > 0 ? (
						<Fragment>{cleanChildren}</Fragment>
					) : (
						<Fragment>
							{ITEMS.map((item) => (
								<DpBenefitGray
									key={item.title}
									title={item.title}
									description={item.description}
									spot={item.spot}
									src={item.icon}
								/>
							))}
						</Fragment>
					)}
				</>
			)}
		</DpBottomContainer>
	);
};

export const BenefitsGray: React.FC<DpBenefitsGrayFrameProps> = (props) => {
	const dpBenefits = useDpBenefits();

	return (
		<DpBenefitsProvider value={dpBenefits}>
			<DpBenefitsGrayFrame {...props} />
			<DpBenefitsDrawer />
		</DpBenefitsProvider>
	);
};
